import { useState, useEffect, useCallback, useRef } from 'react';
import { Workspace } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';

// ─── Live-persist de controles del logo + forceBrandColors ───────────────────
// A diferencia del formData (que se guarda con la barra), estos controles
// persisten de inmediato vía PATCH /logo-scale, con un tick "Saved" inline.

export type LivePersistField = 'logoScale' | 'logoOffsetX' | 'logoOffsetY' | 'forceBrandColors';

export interface UseBrandLivePersistResult {
  /** Valores optimistas actuales (reflejan el workspace + cambios live). */
  logoScale: number;
  logoOffsetX: number;
  logoOffsetY: number;
  forceBrandColors: boolean;
  /** Campo que acaba de guardarse (para mostrar el tick inline), o null. */
  savedField: LivePersistField | null;
  setLogoScale: (value: number) => void;
  setLogoOffset: (offsetX: number, offsetY: number) => void;
  setForceBrandColors: (value: boolean) => void;
}

/**
 * Mantiene un estado optimista de los controles de logo y forceBrandColors, y
 * los persiste inmediatamente con PATCH /api/workspaces/:id/logo-scale. Cada
 * guardado exitoso prende un tick `savedField` que se apaga a los ~1.5s.
 *
 * Reusa la lógica de WorkspacePage (handleLogoScaleChange / OffsetChange /
 * ForceBrandColorsChange) — todos pegan al mismo endpoint /logo-scale.
 */
export function useBrandLivePersist(
  workspaceId: string | undefined,
  workspace: Workspace | null,
): UseBrandLivePersistResult {
  const [logoScale, setLogoScaleState] = useState(workspace?.logoScale ?? 1.0);
  const [logoOffsetX, setLogoOffsetXState] = useState(workspace?.logoOffsetX ?? 0);
  const [logoOffsetY, setLogoOffsetYState] = useState(workspace?.logoOffsetY ?? 0);
  const [forceBrandColors, setForceBrandColorsState] = useState(!!workspace?.forceBrandColors);
  const [savedField, setSavedField] = useState<LivePersistField | null>(null);

  const tickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sincroniza el estado local cuando llega/cambia el workspace remoto.
  useEffect(() => {
    if (!workspace) return;
    setLogoScaleState(workspace.logoScale ?? 1.0);
    setLogoOffsetXState(workspace.logoOffsetX ?? 0);
    setLogoOffsetYState(workspace.logoOffsetY ?? 0);
    setForceBrandColorsState(!!workspace.forceBrandColors);
  }, [workspace]);

  useEffect(() => () => {
    if (tickTimer.current) clearTimeout(tickTimer.current);
  }, []);

  const flashSaved = useCallback((field: LivePersistField) => {
    setSavedField(field);
    if (tickTimer.current) clearTimeout(tickTimer.current);
    tickTimer.current = setTimeout(() => setSavedField(null), 1500);
  }, []);

  const patch = useCallback(async (field: LivePersistField, body: Record<string, unknown>) => {
    if (!workspaceId) return;
    try {
      await apiClient.patch(`/api/workspaces/${workspaceId}/logo-scale`, body);
      flashSaved(field);
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
    }
  }, [workspaceId, flashSaved]);

  const setLogoScale = useCallback((value: number) => {
    setLogoScaleState(value); // optimista
    void patch('logoScale', { logoScale: value });
  }, [patch]);

  const setLogoOffset = useCallback((offsetX: number, offsetY: number) => {
    setLogoOffsetXState(offsetX);
    setLogoOffsetYState(offsetY);
    void patch('logoOffsetX', { logoOffsetX: offsetX, logoOffsetY: offsetY });
  }, [patch]);

  const setForceBrandColors = useCallback((value: boolean) => {
    setForceBrandColorsState(value);
    void patch('forceBrandColors', { forceBrandColors: value });
  }, [patch]);

  return {
    logoScale,
    logoOffsetX,
    logoOffsetY,
    forceBrandColors,
    savedField,
    setLogoScale,
    setLogoOffset,
    setForceBrandColors,
  };
}
